/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "Trigger syncs with Airflow", description = "Learn how to trigger syncs with Hightouch's Airflow Operator.", lastUpdatedDate = new Date(1675728000000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "Trigger syncs with Airflow",
        description: "Learn how to trigger syncs with Hightouch's Airflow Operator.",
        lastUpdatedDate: "Tue Feb 07 2023 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "Overview",
          "depth": 0,
          "id": "overview"
        }, {
          "value": "Setup",
          "depth": 0,
          "id": "setup"
        }, {
          "value": "Create an API key",
          "depth": 1,
          "id": "create-an-api-key"
        }, {
          "value": "Add the Airflow connection",
          "depth": 1,
          "id": "add-the-airflow-connection"
        }, {
          "value": "Set sync schedule type",
          "depth": 1,
          "id": "set-sync-schedule-type"
        }, {
          "value": "Install the Airflow package",
          "depth": 1,
          "id": "install-the-airflow-package"
        }, {
          "value": "Add the HightouchTriggerSyncOperator to your DAG",
          "depth": 1,
          "id": "add-the-hightouchtriggersyncoperator-to-your-dag"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      h2: "h2",
      p: "p",
      a: "a",
      h3: "h3",
      ol: "ol",
      li: "li",
      strong: "strong",
      code: "code",
      img: "img",
      pre: "pre",
      span: "span"
    }, _provideComponents(), props.components), {CreateAPIKeySnippet, APIKeyWarning, SetSyncSchedule} = _components;
    if (!APIKeyWarning) _missingMdxReference("APIKeyWarning", true);
    if (!CreateAPIKeySnippet) _missingMdxReference("CreateAPIKeySnippet", true);
    if (!SetSyncSchedule) _missingMdxReference("SetSyncSchedule", true);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.h2, {
        id: "overview",
        children: "Overview"
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch provides an ", _jsx(_components.a, {
          href: "https://airflow.apache.org/docs/apache-airflow/stable/concepts.html#operators",
          children: "Airflow Operator"
        }), " for triggering syncs via Airflow. The ", _jsx(_components.a, {
          href: "https://github.com/hightouchio/airflow-provider-hightouch",
          children: "Git repository"
        }), " contains the latest code as well as ", _jsx(_components.a, {
          href: "https://github.com/hightouchio/airflow-provider-hightouch#examples",
          children: "example DAGs"
        }), " to help you get started."]
      }), "\n", _jsx(_components.h2, {
        id: "setup",
        children: "Setup"
      }), "\n", _jsx(_components.h3, {
        id: "create-an-api-key",
        children: "Create an API key"
      }), "\n", _jsx(_components.p, {
        children: "The first step is to create a Hightouch API key in your Hightouch workspace settings."
      }), "\n", _jsx(CreateAPIKeySnippet, {}), "\n", _jsx(APIKeyWarning, {}), "\n", _jsx(_components.h3, {
        id: "add-the-airflow-connection",
        children: "Add the Airflow connection"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["In Airflow, create a new connection by going to ", _jsx(_components.strong, {
            children: "Admin > Connections"
          }), " and clicking the ", _jsx(_components.strong, {
            children: "+"
          }), " button to add a new connection."]
        }), "\n", _jsxs(_components.li, {
          children: ["Enter ", _jsx(_components.code, {
            children: "hightouch_default"
          }), " as the ", _jsx(_components.strong, {
            children: "Conn ID"
          }), ", select ", _jsx(_components.strong, {
            children: "HTTP"
          }), " as the ", _jsx(_components.strong, {
            children: "Conn Type"
          }), ", and set the host to ", _jsx(_components.code, {
            children: "https://api.hightouch.com"
          }), ". Optionally, enter a ", _jsx(_components.strong, {
            children: "Description"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Enter the ", _jsx(_components.strong, {
            children: "API key"
          }), " you created in the ", _jsx(_components.strong, {
            children: "Password"
          }), " field."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "integrations/airflow-add-connection.png",
          alt: "Adding a connection in the Airflow UI"
        })
      }), "\n", _jsx(_components.h3, {
        id: "set-sync-schedule-type",
        children: "Set sync schedule type"
      }), "\n", _jsx(SetSyncSchedule, {
        extension: "Airflow"
      }), "\n", _jsx(_components.h3, {
        id: "install-the-airflow-package",
        children: "Install the Airflow package"
      }), "\n", _jsx(_components.p, {
        children: "While the details will vary for your particular Airflow installation, the simplest way to install our package is through pip."
      }), "\n", _jsx(_components.pre, {
        children: _jsx(_components.code, {
          className: "hljs language-bash",
          children: "pip install airflow-provider-hightouch\n"
        })
      }), "\n", _jsx(_components.h3, {
        id: "add-the-hightouchtriggersyncoperator-to-your-dag",
        children: "Add the HightouchTriggerSyncOperator to your DAG"
      }), "\n", _jsx(_components.p, {
        children: "The last step is to add the operator to a DAG and enter the sync ID of the sync you want to trigger. When the task is run, Airflow sends a call to the Hightouch API to trigger a run which completes asynchronously."
      }), "\n", _jsx(_components.p, {
        children: "Alternatively, you can choose to send a synchronous request and await the results of the sync before completing the task."
      }), "\n", _jsxs(_components.p, {
        children: ["For a full description of the operator parameters, ", _jsx(_components.a, {
          href: "https://github.com/hightouchio/airflow-provider-hightouch/blob/main/airflow_provider_hightouch/operators/hightouch.py",
          children: "view the source code in the GitHub repository"
        }), "."]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-python",
          children: [_jsx(_components.span, {
            className: "hljs-keyword",
            children: "from"
          }), " airflow_provider_hightouch.operators.hightouch \\\n    ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "import"
          }), " HightouchTriggerSyncOperator\n\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "with"
          }), " DAG(....) ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "as"
          }), " dag:\n...\n    ", _jsx(_components.span, {
            className: "hljs-comment",
            children: "# This task submits the request but doesn't wait for completion of the sync before completing."
          }), "\n    my_async_task = HightouchTriggerSyncOperator(\n        task_id=", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"run_my_sync\""
          }), ", sync_id=", _jsx(_components.span, {
            className: "hljs-number",
            children: "1234"
          }), "\n        )\n\n    ", _jsx(_components.span, {
            className: "hljs-comment",
            children: "# This task waits for the sync status to complete or error before completing."
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-comment",
            children: "# Warnings can be treated as errors or successes depending on your use case."
          }), "\n    my_sync_task = HightouchTriggerSyncOperator(\n        task_id=", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"run_sync\""
          }), ", sync_id=", _jsx(_components.span, {
            className: "hljs-number",
            children: "1234"
          }), ", synchronous=", _jsx(_components.span, {
            className: "hljs-literal",
            children: "True"
          }), ", error_on_warning=", _jsx(_components.span, {
            className: "hljs-literal",
            children: "True"
          }), "\n    )\n\n"]
        })
      })]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
